function getSeriesPointValue(value) {
  return value &&
    typeof value === 'object' &&
    Object.prototype.hasOwnProperty.call(value, 'y')
    ? value.y
    : value;
}

function toFiniteSeriesNumber(value) {
  const num = Number(getSeriesPointValue(value));
  return Number.isFinite(num) ? num : 0;
}

export function getBgSeriesColor() {
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue('--surface2')
      .trim() || '#f1f5f9'
  );
}

export function hasOnlyNonNegativeBarData(series) {
  const safeSeries = Array.isArray(series) ? series : [];
  const dataSeries = safeSeries
    .map((s) => (Array.isArray(s && s.data) ? s.data : null))
    .filter(Boolean);
  if (!dataSeries.length) return true;

  for (let i = 0; i < dataSeries.length; i += 1) {
    for (let j = 0; j < dataSeries[i].length; j += 1) {
      if (toFiniteSeriesNumber(dataSeries[i][j]) < 0) return false;
    }
  }
  return true;
}

export function getHorizontalBarAxisDataMax(series, isStacked = false) {
  const safeSeries = Array.isArray(series) ? series : [];
  const dataSeries = safeSeries
    .map((s) => (Array.isArray(s && s.data) ? s.data : null))
    .filter(Boolean);
  if (!dataSeries.length) return 0;

  const maxPointCount = dataSeries.reduce(
    (max, values) => Math.max(max, values.length),
    0
  );
  if (isStacked) {
    // Compare bars (category totals), not each segment individually.
    let maxTotal = 0;
    for (let i = 0; i < maxPointCount; i += 1) {
      const categoryTotal = dataSeries.reduce(
        (sum, values) => sum + toFiniteSeriesNumber(values[i]),
        0
      );
      maxTotal = Math.max(maxTotal, categoryTotal);
    }
    return maxTotal;
  }

  let maxValue = 0;
  for (let i = 0; i < dataSeries.length; i += 1) {
    for (let j = 0; j < dataSeries[i].length; j += 1) {
      maxValue = Math.max(maxValue, toFiniteSeriesNumber(dataSeries[i][j]));
    }
  }
  return maxValue;
}

export function getHorizontalBarAxisHeadroomMax(
  series,
  { isStacked = false, stackType = 'normal', headroomRatio = 1.15 } = {}
) {
  const axisMax =
    isStacked && stackType === '100%'
      ? 100
      : getHorizontalBarAxisDataMax(series, isStacked);
  if (!axisMax || axisMax <= 0) return undefined;
  return Math.ceil(axisMax * headroomRatio);
}

export default {
  data() {
    return {
      chartHeight: '185px',
      resizeTimeout: null,
      heightSyncObserver: null,
      heightSyncObserverTarget: null,
      heightSyncObserverRaf: null,
      lastObservedTableHeight: 0,
      themeObserver: null,
    };
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.$nextTick(() => {
      this.setupHeightSyncObserver();
    });
    if (typeof MutationObserver !== 'undefined') {
      this.themeObserver = new MutationObserver(() => {
        this.syncWithCurrentTablePage();
        if (typeof this.onThemeChange === 'function') this.onThemeChange();
      });
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    if (this.heightSyncObserver) {
      this.heightSyncObserver.disconnect();
      this.heightSyncObserver = null;
    }
    this.heightSyncObserverTarget = null;
    this.lastObservedTableHeight = 0;
    if (
      typeof window !== 'undefined' &&
      window.cancelAnimationFrame &&
      this.heightSyncObserverRaf
    ) {
      window.cancelAnimationFrame(this.heightSyncObserverRaf);
    }
    this.heightSyncObserverRaf = null;
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = null;
    }
  },
  methods: {
    // Components using this mixin must return their table ref name.
    getHeightSyncTableRefName() {
      return '';
    },
    // Components using this mixin must return the fallback dataset.
    getHeightSyncFallbackItems() {
      return [];
    },
    // Components using this mixin can return their chart ref name.
    getHeightSyncChartRefName() {
      return '';
    },
    getHeightSyncTableComponent() {
      const refName =
        this.getHeightSyncTableRefName && this.getHeightSyncTableRefName();
      if (!refName || !this.$refs) return null;
      return this.$refs[refName];
    },
    getHeightSyncChartComponent() {
      const refName =
        this.getHeightSyncChartRefName && this.getHeightSyncChartRefName();
      if (!refName || !this.$refs) return null;
      return this.$refs[refName];
    },
    refreshHeightSyncChart() {
      const chart = this.getHeightSyncChartComponent();
      if (!chart || typeof chart.refreshChart !== 'function') return;
      this.$nextTick(() => {
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          window.requestAnimationFrame(() =>
            window.requestAnimationFrame(() => chart.refreshChart())
          );
          return;
        }
        setTimeout(() => chart.refreshChart(), 0);
      });
    },
    getInitialHeightSyncMaxAttempts() {
      return 18;
    },
    getHeightSyncTableContainer(table) {
      if (!table || !table.$el) return null;
      return table.$el.querySelector('.sphere-table-container') || table.$el;
    },
    hasRenderableHeightSyncRows(table) {
      return (
        table &&
        Array.isArray(table.paginatedItems) &&
        table.paginatedItems.length > 0
      );
    },
    isChartHeightSyncReady(chart) {
      return !!(
        chart &&
        chart.$el &&
        chart.$el.classList &&
        chart.$el.classList.contains('base-chart-container') &&
        Math.round(chart.$el.getBoundingClientRect().height) > 0
      );
    },
    isInitialHeightSyncReady() {
      const table = this.getHeightSyncTableComponent();
      const chart = this.getHeightSyncChartComponent();
      if (!this.hasRenderableHeightSyncRows(table)) return false;
      const tableContainer = this.getHeightSyncTableContainer(table);
      const tableHeight = tableContainer
        ? Math.round(tableContainer.getBoundingClientRect().height)
        : 0;
      return tableHeight > 0 && this.isChartHeightSyncReady(chart);
    },
    queueInitialHeightSyncRetry(nextAttempt) {
      this.$nextTick(() => {
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          window.requestAnimationFrame(() => {
            this.scheduleInitialHeightSync(nextAttempt);
          });
          return;
        }
        setTimeout(() => this.scheduleInitialHeightSync(nextAttempt), 16);
      });
    },
    scheduleInitialHeightSync(attempt = 0) {
      const maxAttempts = this.getInitialHeightSyncMaxAttempts();
      if (this.isInitialHeightSyncReady()) {
        this.syncWithCurrentTablePage();
        return;
      }
      if (attempt >= maxAttempts) return;
      this.queueInitialHeightSyncRetry(attempt + 1);
    },
    startInitialHeightSync() {
      this.$nextTick(() => {
        this.scheduleInitialHeightSync();
      });
    },
    updateTablePage(newItems) {
      if (newItems === null || newItems === undefined) {
        return;
      }
      this.$nextTick(() => {
        this.setChartData(newItems || []);
        this.deferLayoutSync();
        this.setupHeightSyncObserver();
      });
    },
    handleResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.deferLayoutSync();
      }, 100);
    },
    syncWithCurrentTablePage() {
      this.setupHeightSyncObserver();
      const table = this.getHeightSyncTableComponent();
      const fallbackItems = this.getHeightSyncFallbackItems() || [];
      if (table && Array.isArray(table.paginatedItems)) {
        this.setChartData(table.paginatedItems);
        this.deferLayoutSync();
      } else if (!table) {
        this.setChartData(fallbackItems);
        this.deferLayoutSync();
      }
    },
    getHeightSyncGridElement(table) {
      return table && table.$el ? table.$el.querySelector('.k-grid') : null;
    },
    getHeightSyncContainerElement(table) {
      return table && table.$el
        ? table.$el.querySelector('.sphere-table-container')
        : null;
    },
    firstDefinedHeightSyncElement(elements) {
      for (let i = 0; i < elements.length; i += 1) {
        if (elements[i]) return elements[i];
      }
      return null;
    },
    getHeightSyncTargetElement(table, gridAligned, mode = 'measure') {
      if (!table || !table.$el) return null;
      const kGrid = this.getHeightSyncGridElement(table);
      const containerEl = this.getHeightSyncContainerElement(table);
      if (gridAligned) {
        return this.firstDefinedHeightSyncElement([
          kGrid,
          containerEl,
          table.$el,
        ]);
      }
      return mode === 'measure'
        ? this.firstDefinedHeightSyncElement([containerEl, table.$el])
        : this.firstDefinedHeightSyncElement([kGrid, containerEl, table.$el]);
    },
    queueObservedLayoutSync() {
      const sync = () => {
        this.heightSyncObserverRaf = null;
        this.deferLayoutSync();
      };
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        if (this.heightSyncObserverRaf) {
          window.cancelAnimationFrame(this.heightSyncObserverRaf);
        }
        this.heightSyncObserverRaf = window.requestAnimationFrame(sync);
        return;
      }
      setTimeout(sync, 0);
    },
    isHeightSyncObserverSupported() {
      return (
        typeof window !== 'undefined' && typeof ResizeObserver !== 'undefined'
      );
    },
    ensureHeightSyncObserver() {
      if (this.heightSyncObserver) return;
      this.heightSyncObserver = new ResizeObserver((entries) => {
        if (!entries || !entries.length) return;
        const nextHeight = Math.round(entries[0].contentRect.height || 0);
        if (!nextHeight || nextHeight === this.lastObservedTableHeight) return;
        this.lastObservedTableHeight = nextHeight;
        this.queueObservedLayoutSync();
      });
    },
    observeHeightSyncTarget(target) {
      if (this.heightSyncObserverTarget === target) return;
      if (this.heightSyncObserverTarget) {
        this.heightSyncObserver.unobserve(this.heightSyncObserverTarget);
      }
      this.heightSyncObserverTarget = target;
      this.heightSyncObserver.observe(target);
    },
    setupHeightSyncObserver() {
      if (!this.isHeightSyncObserverSupported()) return;
      const table = this.getHeightSyncTableComponent();
      if (!table || !table.$el) return;
      const target = this.getHeightSyncTargetElement(
        table,
        this.useGridAlignedHeightSync(),
        'observe'
      );
      if (!target) return;

      this.ensureHeightSyncObserver();
      this.observeHeightSyncTarget(target);
      this.queueObservedLayoutSync();
    },
    deferLayoutSync() {
      this.$nextTick(() => {
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          window.requestAnimationFrame(() =>
            window.requestAnimationFrame(() => this.syncHeightsForRows())
          );
          return;
        }
        setTimeout(() => this.syncHeightsForRows(), 0);
      });
    },
    getTopControlHeight() {
      const control = this.$el.querySelector('.base-card_top-of-body');
      if (!control) return 0;
      const style = window.getComputedStyle(control);
      const marginBottom = parseFloat(style.marginBottom || 0);
      return control.getBoundingClientRect().height + marginBottom;
    },
    // Components can override this to opt-in to grid-aligned sync, where the
    // chart matches the visible .k-grid card height (excluding the table
    // toolbar) and is pushed down by the toolbar height so its bordered card
    // visually aligns with the .k-grid top. Default keeps the original
    // behavior so consumers that have not been validated for the new logic
    // are unaffected.
    useGridAlignedHeightSync() {
      return false;
    },
    getHeightSyncTargetInfo(table, gridAligned) {
      const toolbar = table.$el.querySelector('.k-table-actions');
      const target = this.getHeightSyncTargetElement(
        table,
        gridAligned,
        'measure'
      );
      if (!target) return null;
      const tableVisualHeight = Math.round(
        target.getBoundingClientRect().height
      );
      if (!tableVisualHeight) return null;
      return { toolbar, target, tableVisualHeight };
    },
    setHeightSyncChartPadding(gridAligned, toolbar) {
      const slotChart = this.$el.querySelector('.slot-chart');
      if (!slotChart) return;
      const toolbarHeight =
        gridAligned && toolbar
          ? Math.round(toolbar.getBoundingClientRect().height)
          : 0;
      slotChart.style.paddingTop = toolbarHeight ? `${toolbarHeight}px` : '';
    },
    applyHeightSyncCorrection(target, topControlHeight, initialChartHeight) {
      this.$nextTick(() => {
        const chartContainer = this.$el.querySelector(
          '.slot-chart .base-chart-container'
        );
        if (!chartContainer) return;
        const targetRect = target.getBoundingClientRect();
        const currentTotalChartHeight = Math.round(
          topControlHeight + chartContainer.getBoundingClientRect().height
        );
        const heightDiff = Math.round(
          targetRect.height - currentTotalChartHeight
        );
        if (Math.abs(heightDiff) <= 1) return;
        const currentHeight =
          parseInt(this.chartHeight, 10) || initialChartHeight;
        this.chartHeight = `${Math.max(120, currentHeight + heightDiff)}px`;
        this.refreshHeightSyncChart();
      });
    },
    syncHeightsForRows() {
      if (!this.isInitialHeightSyncReady()) return;
      const table = this.getHeightSyncTableComponent();
      if (!table || !table.$el) return;

      const gridAligned = this.useGridAlignedHeightSync();
      const targetInfo = this.getHeightSyncTargetInfo(table, gridAligned);
      if (!targetInfo) return;

      this.setHeightSyncChartPadding(gridAligned, targetInfo.toolbar);

      const topControlHeight = this.getTopControlHeight();
      const initialChartHeight = Math.max(
        120,
        Math.round(targetInfo.tableVisualHeight - topControlHeight)
      );
      this.chartHeight = `${initialChartHeight}px`;
      this.refreshHeightSyncChart();

      this.applyHeightSyncCorrection(
        targetInfo.target,
        topControlHeight,
        initialChartHeight
      );
    },
  },
};
